import React, { useCallback, useMemo } from 'react';
import { Components, ExtendedComponentSchema } from 'formiojs';
import {
  EhsFormioReactComponent,
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { TimeInput } from '../../../form';
import { intl } from '../../../../lib';
import { LanguageProperty } from '../../../generated/LanguageProperty';
import { Component } from '@formio/react';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type FormioTimeInputConstructor = Record<string, never>;
export type FormioTimeInputSettings = Record<string, never>;
export const FormioTimeInputType = 'ehstimeinput';
export class EhsFormioTimeInput extends EhsFormioReactComponent<
  FormioTimeInputSettings,
  FormioTimeInputConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: FormioTimeInputType,
      label: 'common.time',
      key: FormioTimeInputType,
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-Timeinput',
      group: 'advanced',
      icon: 'calendar',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioTimeInput.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  extractFields(component: Component<FormioTimeInputConstructor>): void {
    // nothing
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<FormioTimeInputSettings>) => JSX.Element,
    FormioTimeInputSettings
  ] {
    return [TimeInputCmp, {}];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link FormioTimeInputSettings} properties
 */
function TimeInputCmp({
  value,
  onChange,
  label,
  readOnly,
  settings
}: EhsFormioReactProps<FormioTimeInputSettings>): JSX.Element {
  const onChangeWrapper = useCallback(
    (val: string): void => {
      onChange(val);
    },
    [onChange]
  );
  const wrapperValue = useMemo(() => {
    return value ?? '';
  }, [value]);
  return useMemo(() => {
    return (
      <TimeInput
        onChange={onChangeWrapper}
        value={wrapperValue}
        label={intl(label as LanguageProperty)}
        disabled={readOnly}
      />
    );
  }, [label, onChangeWrapper, readOnly, wrapperValue]);
}
